import { isScenario } from 'components/scenarios/utils/scenario-type'
import {
  getOwningTeamLabel,
  type ScenarioForTestSuite
} from 'components/test-suites/forms/test-suite-form-utils'

import type { Team } from 'types/api/teams'

/**
 * These values allow for easier filtering on the TestSuiteScenarioOption.
 * The `filterBy` property will contain one of these values after calculation.
 */
export enum ScenarioTypeFilterBy {
  MyTeam = 'MyTeam',
  OtherTeams = 'OtherTeams',
  RecommendedAndShared = 'RecommendedAndShared'
}

export type TestSuiteScenarioOption = {
  filterBy: ScenarioTypeFilterBy
  guid: string
  label: string
  orgId: string | undefined
  owningTeamLabel: string | null
  section: string
}

/**
 * Filtering works by checking the `filterBy` key on the available scenarios.
 * If the menu selection is "All", then it ignores the `filterBy` and shows all options.
 */
export type FilterOptionKey = ScenarioTypeFilterBy | 'all'

export const FILTER_OPTIONS: {
  key: FilterOptionKey
  label: string
}[] = [
  {
    key: 'all',
    label: 'All'
  },
  {
    key: ScenarioTypeFilterBy.MyTeam,
    label: 'My Team'
  },
  {
    key: ScenarioTypeFilterBy.RecommendedAndShared,
    label: 'Recommended & Shared'
  },
  {
    key: ScenarioTypeFilterBy.OtherTeams,
    label: 'Other Teams'
  }
]

export function getScenarioTypeFilterBy(
  isCurrentTeam: boolean,
  owningTeam?: Team
) {
  if (isCurrentTeam) {
    return ScenarioTypeFilterBy.MyTeam
  }

  if (!isCurrentTeam && owningTeam) {
    return ScenarioTypeFilterBy.OtherTeams
  }

  return ScenarioTypeFilterBy.RecommendedAndShared
}

export function generateTestSuiteScenarioOptions(
  scenarios: ScenarioForTestSuite[],
  teamIdMap: Record<string, Team>,
  currentTeamId: string | undefined
): TestSuiteScenarioOption[] {
  return scenarios
    .map((scenario: ScenarioForTestSuite) => {
      const teamId = isScenario(scenario) ? scenario.org_id : undefined
      const isCurrentTeam = teamId === currentTeamId
      const owningTeam = teamId ? teamIdMap[teamId] : undefined
      const { name: owningTeamName } = owningTeam ?? {}

      return {
        section: owningTeamName ?? 'Recommended & Shared',
        filterBy: getScenarioTypeFilterBy(isCurrentTeam, owningTeam),
        guid: scenario.guid,
        label: scenario.name,
        orgId: teamId,
        owningTeamLabel: getOwningTeamLabel(teamId, owningTeam)
      }
    })
    .sort(
      (
        testSuiteScenarioOptionA: TestSuiteScenarioOption,
        testSuiteScenarioOptionB: TestSuiteScenarioOption
      ) => {
        const predicates = [
          ({ orgId }: TestSuiteScenarioOption) => orgId === currentTeamId,
          ({ section }: TestSuiteScenarioOption) =>
            section === ScenarioTypeFilterBy.RecommendedAndShared
        ]

        // order: current team, recommended & shared, others
        for (const predicate of predicates) {
          const predicateA = predicate(testSuiteScenarioOptionA)
          const predicateB = predicate(testSuiteScenarioOptionB)

          if (predicateA && predicateB) {
            return 0
          }

          if (predicateA || predicateB) {
            return predicateA ? -1 : 1
          }
        }

        return 0
      }
    )
}
